import { useEffect, useState } from 'react';
import { Mail, Clock, Database, Save, Forward } from 'lucide-react';
import { useToastStore } from '../store';
import { Spinner, Modal } from '../components/ui';
import { api } from '../utils/api';

export function AdminSettingsPage() {
  const { addToast } = useToastStore();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [hrIntegrations, setHrIntegrations] = useState<any[]>([]);
  const [showHRModal, setShowHRModal] = useState(false);
  const [editingHR, setEditingHR] = useState<any>(null);

  useEffect(() => {
    loadSettings();
    loadHRIntegrations();
  }, []);

  const loadSettings = async () => {
    try {
      const result = await api.getSettings();
      if (result.success && result.data) {
        setSettings(result.data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      addToast('Failed to load settings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadHRIntegrations = async () => {
    try {
      const result = await api.getHRIntegrations();
      if (result.success && result.data) {
        setHrIntegrations(result.data);
      }
    } catch (error) {
      console.error('Failed to load HR integrations:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await api.updateSettings(settings);
      if (result.success) {
        addToast('Settings saved successfully', 'success');
      } else {
        addToast(result.error || 'Failed to save settings', 'error');
      }
    } catch (error) {
      addToast('Failed to save settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleHRSave = async (data: any) => {
    try {
      const result = await api.saveHRIntegration(data);
      if (result.success) {
        addToast('HR integration saved successfully', 'success');
        setShowHRModal(false);
        setEditingHR(null);
        loadHRIntegrations();
      } else {
        addToast(result.error || 'Failed to save HR integration', 'error');
      }
    } catch (error) {
      addToast('Failed to save HR integration', 'error');
    }
  };

  const handleHRDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this HR integration?')) return;
    
    try {
      const result = await api.deleteHRIntegration(id);
      if (result.success) {
        addToast('HR integration deleted successfully', 'success');
        loadHRIntegrations();
      } else {
        addToast(result.error || 'Failed to delete HR integration', 'error');
      }
    } catch (error) {
      addToast('Failed to delete HR integration', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage system settings and integrations</p>
      </div>

      {/* Email Settings */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-slate-900">Email Integration</h2>
          </div>
          
          <div className="space-y-4">
          <div>
            <label className="input-label">Email Provider</label>
            <select
              value={settings.email_provider || 'resend'}
              onChange={(e) => setSettings({ ...settings, email_provider: e.target.value })}
              className="input"
            >
              <option value="resend">Resend (API)</option>
              <option value="smtp">SMTP</option>
            </select>
          </div>

          {settings.email_provider === 'smtp' ? (
            <>
              <div>
                <label className="input-label">SMTP Host</label>
                <input
                  type="text"
                  value={settings.smtp_host || ''}
                  onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                  className="input"
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div>
                <label className="input-label">SMTP Port</label>
                <input
                  type="text"
                  value={settings.smtp_port || '587'}
                  onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                  className="input"
                  placeholder="587"
                />
              </div>
              <div>
                <label className="input-label">SMTP Username</label>
                <input
                  type="text"
                  value={settings.smtp_user || ''}
                  onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                  className="input"
                  placeholder="your-email@gmail.com"
                />
              </div>
              <div>
                <label className="input-label">SMTP Password</label>
                <input
                  type="password"
                  value={settings.smtp_pass === '***' ? '' : (settings.smtp_pass || '')}
                  onChange={(e) => setSettings({ ...settings, smtp_pass: e.target.value })}
                  className="input"
                  placeholder="Leave blank to keep current"
                />
              </div>
              <div>
                <label className="input-label">SMTP Secure</label>
                <select
                  value={settings.smtp_secure || 'false'}
                  onChange={(e) => setSettings({ ...settings, smtp_secure: e.target.value })}
                  className="input"
                >
                  <option value="false">False</option>
                  <option value="true">True</option>
                </select>
              </div>
            </>
          ) : (
            <div>
              <label className="input-label">Resend API Key</label>
              <input
                type="password"
                value={settings.resend_api_key === '***' ? '' : (settings.resend_api_key || '')}
                onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })}
                className="input"
                placeholder="Leave blank to keep current"
              />
            </div>
          )}

          <div>
            <label className="input-label">From Email</label>
            <input
              type="email"
              value={settings.email_from || ''}
              onChange={(e) => setSettings({ ...settings, email_from: e.target.value })}
              className="input"
              placeholder="noreply@example.com"
            />
          </div>
          <div>
            <label className="input-label">From Name</label>
            <input
              type="text"
              value={settings.email_from_name || ''}
              onChange={(e) => setSettings({ ...settings, email_from_name: e.target.value })}
              className="input"
              placeholder="Shift Scheduler"
            />
          </div>
        </div>
        </div>
      </div>

      {/* Email Notifications */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-slate-900">Email Notifications</h2>
          </div>
          
          <div className="space-y-3">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_notifications_enabled === '1'}
              onChange={(e) => setSettings({ ...settings, email_notifications_enabled: e.target.checked ? '1' : '0' })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Enable all email notifications</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_shift_assigned === '1'}
              onChange={(e) => setSettings({ ...settings, email_shift_assigned: e.target.checked ? '1' : '0' })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Send email when shift is assigned</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_shift_reminder === '1'}
              onChange={(e) => setSettings({ ...settings, email_shift_reminder: e.target.checked ? '1' : '0' })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Send email reminders for shifts</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_request_status === '1'}
              onChange={(e) => setSettings({ ...settings, email_request_status: e.target.checked ? '1' : '0' })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Send email when request status changes</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_admin_request_notification === '1'}
              onChange={(e) => setSettings({ ...settings, email_admin_request_notification: e.target.checked ? '1' : '0' })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span>Send email to admins when request is submitted</span>
          </label>
          </div>
        </div>
      </div>

      {/* Clock In Settings */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-slate-900">Clock In/Out Settings</h2>
          </div>
          
          <div>
          <label className="input-label">Clock In Time Window (minutes)</label>
          <input
            type="number"
            value={settings.clock_in_time_window_minutes || ''}
            onChange={(e) => setSettings({ ...settings, clock_in_time_window_minutes: e.target.value })}
            className="input"
            placeholder="Leave empty to allow clock in anytime"
          />
          <p className="text-sm text-slate-500 mt-1">
            Users can only clock in within this many minutes before their shift starts. Leave empty to allow clock in anytime.
          </p>
        </div>
        </div>
      </div>

      {/* Timezone Settings */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-slate-900">Timezone Settings</h2>
          </div>
          
          <div>
          <label className="input-label">Timezone</label>
          <select
            value={settings.timezone || 'UTC'}
            onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
            className="input"
          >
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time (America/New_York)</option>
            <option value="America/Chicago">Central Time (America/Chicago)</option>
            <option value="America/Denver">Mountain Time (America/Denver)</option>
            <option value="America/Los_Angeles">Pacific Time (America/Los_Angeles)</option>
            <option value="America/Phoenix">Arizona Time (America/Phoenix)</option>
            <option value="America/Anchorage">Alaska Time (America/Anchorage)</option>
            <option value="Pacific/Honolulu">Hawaii Time (Pacific/Honolulu)</option>
            <option value="Europe/London">London (Europe/London)</option>
            <option value="Europe/Paris">Paris (Europe/Paris)</option>
            <option value="Europe/Berlin">Berlin (Europe/Berlin)</option>
            <option value="Asia/Tokyo">Tokyo (Asia/Tokyo)</option>
            <option value="Asia/Shanghai">Shanghai (Asia/Shanghai)</option>
            <option value="Australia/Sydney">Sydney (Australia/Sydney)</option>
            <option value="America/Toronto">Toronto (America/Toronto)</option>
            <option value="America/Vancouver">Vancouver (America/Vancouver)</option>
            <option value="America/Mexico_City">Mexico City (America/Mexico_City)</option>
            <option value="America/Sao_Paulo">São Paulo (America/Sao_Paulo)</option>
          </select>
          <p className="text-sm text-slate-500 mt-1">
            This timezone is used to determine who is currently on shift. All shift times are interpreted in this timezone.
          </p>
        </div>
        </div>
      </div>

      {/* Email Relay */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Forward className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-slate-900">Email Relay</h2>
          </div>
          
          <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-900 font-medium mb-2">How Email Relay Works</p>
            <p className="text-sm text-blue-800">
              When emails are sent to your configured incoming address, they are automatically forwarded to users currently on shift (TO) and CC'd to all admins. 
              If no one is on shift, emails are forwarded to admins only.
            </p>
          </div>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.email_relay_enabled === '1'}
              onChange={(e) => setSettings({ ...settings, email_relay_enabled: e.target.checked ? '1' : '0' })}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <span className="font-medium">Enable Email Relay</span>
          </label>

          <div>
            <label className="input-label">Incoming Email Address</label>
            <input
              type="email"
              value={settings.email_relay_incoming_address || ''}
              onChange={(e) => setSettings({ ...settings, email_relay_incoming_address: e.target.value })}
              className="input"
              placeholder="support@yourdomain.com"
            />
            <p className="text-sm text-slate-500 mt-1">
              The email address that will receive incoming emails. This must match the address configured in your email service (e.g., Resend).
              Only emails sent to this address will be relayed.
            </p>
          </div>

          <div>
            <label className="input-label">Webhook Secret</label>
            <input
              type="password"
              value={settings.email_relay_webhook_secret === '***' ? '' : (settings.email_relay_webhook_secret || '')}
              onChange={(e) => setSettings({ ...settings, email_relay_webhook_secret: e.target.value })}
              className="input"
              placeholder="Leave blank to keep current"
            />
            <p className="text-sm text-slate-500 mt-1">
              Secret key used to authenticate webhook requests from your email service. 
              Use this same secret when configuring the webhook in Resend (or your email service) as the <code className="bg-slate-100 px-1 rounded">X-Webhook-Secret</code> header.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-slate-900 font-medium mb-2">Setup Instructions</p>
            <ol className="text-sm text-slate-700 space-y-1 list-decimal list-inside">
              <li>Create an email address in Resend (or your email service) for your domain</li>
              <li>Configure the webhook URL: <code className="bg-white px-1 rounded">https://schedule.pahatzalah.com/api/email-relay/webhook</code></li>
              <li>Add the webhook secret as a header: <code className="bg-white px-1 rounded">X-Webhook-Secret: [your-secret]</code></li>
              <li>Enter the email address and webhook secret above</li>
              <li>Enable email relay and save settings</li>
            </ol>
          </div>
        </div>
        </div>
      </div>

      {/* HR Integrations */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-slate-900">HR Platform Integrations</h2>
            </div>
            <button
              onClick={() => {
                setEditingHR(null);
                setShowHRModal(true);
              }}
              className="btn btn-primary btn-sm"
            >
              Add Integration
            </button>
          </div>
          
          {hrIntegrations.length === 0 ? (
            <p className="text-slate-500">No HR integrations configured</p>
          ) : (
            <div className="space-y-2">
            {hrIntegrations.map((integration) => (
              <div key={integration.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">{integration.platform_name}</p>
                  <p className="text-sm text-slate-500">
                    {integration.base_url || 'No URL configured'}
                    {integration.enabled ? ' • Enabled' : ' • Disabled'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingHR(integration);
                      setShowHRModal(true);
                    }}
                    className="btn btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleHRDelete(integration.id)}
                    className="btn btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>


      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn btn-primary"
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          )}
        </button>
      </div>

      {/* HR Integration Modal */}
      <Modal
        isOpen={showHRModal}
        onClose={() => {
          setShowHRModal(false);
          setEditingHR(null);
        }}
        title={editingHR ? 'Edit HR Integration' : 'Add HR Integration'}
      >
        <HRIntegrationForm
          integration={editingHR}
          onSave={handleHRSave}
          onCancel={() => {
            setShowHRModal(false);
            setEditingHR(null);
          }}
        />
      </Modal>
    </div>
  );
}

function HRIntegrationForm({ integration, onSave, onCancel }: { integration: any; onSave: (data: any) => void; onCancel: () => void }) {
  const [formData, setFormData] = useState({
    platform_name: integration?.platform_name || '',
    api_key: '',
    api_secret: '',
    base_url: integration?.base_url || '',
    enabled: integration?.enabled || false,
    config_json: integration?.config_json ? JSON.stringify(integration.config_json, null, 2) : ''
  });

  const handleSubmit = () => {
    if (!formData.platform_name) {
      alert('Platform name is required');
      return;
    }

    const data: any = {
      ...formData,
      id: integration?.id,
      config_json: formData.config_json ? JSON.parse(formData.config_json) : null
    };

    // Don't send api_key/api_secret if they're masked
    if (formData.api_key === '***') delete data.api_key;
    if (formData.api_secret === '***') delete data.api_secret;

    onSave(data);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="input-label">Platform Name *</label>
        <input
          type="text"
          value={formData.platform_name}
          onChange={(e) => setFormData({ ...formData, platform_name: e.target.value })}
          className="input"
          placeholder="e.g., BambooHR, Workday"
        />
      </div>
      <div>
        <label className="input-label">API Key</label>
        <input
          type="password"
          value={formData.api_key}
          onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
          className="input"
          placeholder="Leave blank to keep current"
        />
      </div>
      <div>
        <label className="input-label">API Secret</label>
        <input
          type="password"
          value={formData.api_secret}
          onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
          className="input"
          placeholder="Leave blank to keep current"
        />
      </div>
      <div>
        <label className="input-label">Base URL</label>
        <input
          type="text"
          value={formData.base_url}
          onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
          className="input"
          placeholder="https://api.example.com"
        />
      </div>
      <div>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={formData.enabled}
            onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            className="w-4 h-4 text-primary-600 rounded"
          />
          <span>Enabled</span>
        </label>
      </div>
      <div>
        <label className="input-label">Config JSON (optional)</label>
        <textarea
          value={formData.config_json}
          onChange={(e) => setFormData({ ...formData, config_json: e.target.value })}
          className="input"
          rows={4}
          placeholder='{"key": "value"}'
        />
      </div>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button onClick={handleSubmit} className="btn btn-primary">
          Save
        </button>
      </div>
    </div>
  );
}

